import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { provideAuth } from '@core/auth';

import { environment } from '../../environments/environment';
import { provideCoreConfig } from '../core/config';
import { resolveCoreRuntimeConfig } from '../core/config/core-config';
import { InMemoryTokenStore } from '../core/auth/token/in-memory-token-store';
import { SessionTokenStore } from '../core/auth/token/session-token-store';
import { HybridTokenStore, TokenStore } from '../core/auth/token/token-store';
import {
  BeaconTelemetryService,
  resolveBeaconTelemetrySinkUrl,
} from '../core/observability/beacon-telemetry.service';
import { ConsoleTelemetryService } from '../core/observability/console-telemetry.service';
import { NoopTelemetryService } from '../core/observability/noop-telemetry.service';
import { TelemetryService } from '../core/observability/telemetry.service';
import { CORE_RUNTIME_CONFIG } from '../core/config/core-config.token';
import { TelemetrySink } from '../core/config/core-config';
import { MAT_DATE_LOCALE } from '@angular/material/core';

/**
 * Provides core application services and configuration.
 * This includes HTTP interceptors, auth, logging, and global config.
 */
export function provideCoreServices(): EnvironmentProviders {
  const resolvedConfig = resolveCoreRuntimeConfig(environment.core);
  const isSessionFallback = resolvedConfig.tokenStorageMode === 'memory-session-fallback';

  if (isSessionFallback && environment.production) {
    throw new Error(
      '[Core] SessionTokenStore (memory-session-fallback) must not be used in production environments — XSS risk. Set tokenStorageMode to "memory" in the production environment config.'
    );
  }

  return makeEnvironmentProviders([
    // Environment configuration
    { provide: 'ENVIRONMENT', useValue: environment },

    provideCoreConfig(environment.core),

    // Auth (mock or real based on environment.enableMocks)
    provideAuth(),

    InMemoryTokenStore,
    ...(isSessionFallback ? [SessionTokenStore] : []),
    {
      provide: TokenStore,
      useFactory: (inMemory: InMemoryTokenStore, session?: SessionTokenStore) => {
        if (isSessionFallback) {
          return new HybridTokenStore(inMemory, session!);
        }
        return inMemory;
      },
      deps: isSessionFallback ? [InMemoryTokenStore, SessionTokenStore] : [InMemoryTokenStore],
    },
    {
      provide: TelemetryService,
      useFactory: (
        config: { telemetrySink: TelemetrySink },
        appEnvironment: { apiBaseUrl?: string }
      ) => {
        switch (config.telemetrySink) {
          case 'beacon':
            return new BeaconTelemetryService(
              resolveBeaconTelemetrySinkUrl(appEnvironment.apiBaseUrl)
            );
          case 'none':
            return new NoopTelemetryService();
          case 'console':
          default:
            return new ConsoleTelemetryService();
        }
      },
      deps: [CORE_RUNTIME_CONFIG, 'ENVIRONMENT'],
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => navigator.language
    }

    // TODO: Add more core services as they're implemented:
    // - provideHttpInterceptors()
    // - provideLogging()
    // - provideTenant()
  ]);
}
